import { Injectable, OnDestroy } from '@angular/core';
import { Store, createAction, props } from '@ngrx/store';
import { MutationResult } from 'apollo-angular';
import { DialogService } from 'primeng/dynamicdialog';
import { default as printJS } from 'print-js';
import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { BaseInvoiceFragment, GetAegisDocumentLinkGQL, ResendDocumentGQL, ResendDocumentMutationVariables, SendInvoicesGQL, SendInvoicesMutation, TransactionsGQL } from '../../generated/graphql.generated';

import { determineArtifactType } from '../artifacts/artifact.util';
import { PandaDocService } from '../features/public-api';
import { Artifact } from '../interfaces/artifacts';
import { isFinalizedInvoice } from '../invoices/invoices.utils';
import { JobInvoiceActions } from '../job/job-invoice/job-invoice.actions';
import { ApplyTransactionComponent } from '../transactions/apply-transaction/apply-transaction.component';

import { DetailsHelperService } from './details-helper.service';
import { FreyaHelperService, ViewableArtifact } from './freya-helper.service';
import { FreyaMutateService } from './freya-mutate.service';
import { FreyaNotificationsService } from './freya-notifications.service';
import { ResponsiveHelperService } from './responsive-helper.service';


export const confirmSendInvoiceDialogAcceptButtonClicked = createAction(
  '[Confirm Send Invoice] Accept Button Clicked',
  props<{ invoiceId: string; }>(),
)

export const payInvoiceRequested = createAction(
  '[Pay Invoice] Requested',
  props<{ invoice: BaseInvoiceFragment; defaultAmount?: number }>(),
)

@Injectable({
  providedIn: 'root'
})
export class DocumentHelperService implements OnDestroy {

  subs = new SubSink();

  constructor(
    private localNotify: FreyaNotificationsService,
    private dialogService: DialogService,
    private responsiveHelper: ResponsiveHelperService,
    private freyaHelper: FreyaHelperService,
    private pandadocService: PandaDocService,
    private getAegisDocumentLinkGQL: GetAegisDocumentLinkGQL,
    private resendDocumentGQL: ResendDocumentGQL,
    private sendInvoicesGQL: SendInvoicesGQL,
    private detailsHelper: DetailsHelperService,
    private freyaMutate: FreyaMutateService,
    private transactionsGQL: TransactionsGQL,
    private store: Store,
  ) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  printDocument(artifact: ViewableArtifact) {
    const printableType = this.getPrintableType(artifact);

    if (!printableType) { return; }

    printJS({
      printable: artifact.signedUrl || artifact.url,
      type: printableType,
      onError: (err) => this.localNotify.error(err)
    });
  }

  getPrintableType(artifact: ViewableArtifact) {
    if (!artifact.contentType) {
      return false;
    }

    const [_content, docType] = artifact.contentType.split('/');
    if (docType === 'pdf') {
      return 'pdf';
    }
    if (determineArtifactType(artifact.contentType) === 'Image') {
      return 'image';
    }
    return false;
  }

  warnInvoiceOutdated(reason: string) {
    this.localNotify.warning('This invoice is outdated', reason, 10000);
  }

  downloadDocument(artifact: ViewableArtifact, options: { outdated?: boolean } = {}) {
    if (!artifact) { return; }
    const url = artifact.signedUrl || artifact.url;
    const name = options.outdated
      ? artifact.name?.replace(/(\.pdf)?$/i, ' (outdated)$1')
      : artifact.name;
    this.freyaHelper.downloadFile(url, name);
  }

  /**
   * Open a document in pandadoc
   */
  openInPandadoc(artifact: ViewableArtifact, role: 'customer' | 'employee') {
    this.subs.sink = this.pandadocService.getDocumentLink({ artifactId: artifact.id, role }).subscribe((res) => {
      this.freyaHelper.openInDialog(artifact, res.data.getPandadocDocumentLink);
    }, (err) => {
      this.localNotify.apolloError('Failed to open document', err);
    });
  }

  /**
   * Open the tokenized Aegis sign page for a document in the in-app dialog (same
   * as PandaDoc). The backend mints a role-scoped token so the sign page
   * activates only that role's block.
   */
  openAegisSignLink(artifact: ViewableArtifact, role: string) {
    this.subs.sink = this.getAegisDocumentLinkGQL.fetch(
      { artifactId: artifact.id, role },
      { fetchPolicy: 'no-cache' },
    ).subscribe((res) => {
      const link = res.data?.getAegisDocumentLink;
      if (!link) {
        this.localNotify.error('Failed to open document', 'No sign link was returned');
        return;
      }
      this.freyaHelper.openInDialog(artifact, link);
    }, (err) => {
      this.localNotify.apolloError('Failed to open document', err);
    });
  }

  resendDocument(artifact: Artifact) {

    const resendDocumentInput: ResendDocumentMutationVariables = {
      artifactId: artifact.id,
      subject: artifact.name,
      message: artifact?.metadata?.message,
    };

    this.resendDocumentGQL.mutate(resendDocumentInput)
      .subscribe(() => {
        this.localNotify.success('Document resent to customer');
      }, (err) => {
        this.localNotify.apolloError(`Could not resend document`, err);
        console.error(err);
      });
  }

  confirmSendInvoice<T extends BaseInvoiceFragment>(invoice: T) {

    if (isFinalizedInvoice(invoice)) {
      this.sendInvoice(invoice);
    } else {
      this.showSendInvoiceConfirmation(invoice);
    }
  }

  showSendInvoiceConfirmation<T extends BaseInvoiceFragment>(invoice: T) {

    const message = `Sending an invoice will finalize it. `
      + `You will not be able to make any further charges on any invoiced events. `;

    const sendInvoice$ = of(invoice.id).pipe(
      tap((invoiceId) => this.store.dispatch(confirmSendInvoiceDialogAcceptButtonClicked({ invoiceId }))),
      switchMap((invoiceId) => this.sendInvoicesGQL.mutate({ invoiceIds: [ invoiceId ]})),
    );

    this.freyaHelper.confirmWithLoading({
      header: 'Send Invoice?',
      message,
      acceptLabel: 'Send and Finalize',
      acceptIcon: 'pi pi-send',
      rejectLabel: 'Cancel',
      rejectIcon: 'pi pi-times',
      acceptObservable: sendInvoice$,
      onAcceptSuccess: (res: MutationResult<SendInvoicesMutation> ) => {

        this.localNotify.success('Invoice sent');

        this.detailsHelper.pushUpdate({
          id: invoice.id,
          type: 'Invoice',
          action: 'update',
        });

        this.store.dispatch(JobInvoiceActions.sendInvoiceSuccess({
          invoiceId: invoice.id,
          jobId: invoice.job.id,
          updated: res.data.sendInvoices.updated,
        }));
      },
      onAcceptErr: (err) => {
        this.localNotify.apolloError('Failed to send invoice', err);
      }
    });
  }

  sendInvoice<T extends BaseInvoiceFragment>(invoice: T) {

    this.localNotify.info('Sending...', 'This may take a moment');

    this.sendInvoicesGQL.mutate({ invoiceIds: [invoice.id] }).subscribe((res) => {

      this.localNotify.success('Invoice sent');

      this.detailsHelper.pushUpdate({
        id: invoice.id,
        type: 'Invoice',
        action: 'update',
      });

      this.store.dispatch(JobInvoiceActions.sendInvoiceSuccess({
        invoiceId: invoice.id,
        jobId: invoice.job.id,
        updated: res.data.sendInvoices.updated,
      }));

    }, ((error) => {
      this.localNotify.apolloError('Failed to send invoice', error);
      this.store.dispatch(JobInvoiceActions.sendInvoiceError({
        invoiceId: invoice.id,
        error,
      }))
    }));

  }

  openCreateInvoiceDialog(jobId: string) {
    this.freyaMutate.openMutateObject({
      mutateType: 'create',
      objectType: 'invoice',
      additionalValues: [
        {
          property: 'jobId',
          value: jobId,
        }
      ],
    });
  }

  async payInvoice(
    invoice: BaseInvoiceFragment,
    defaultAmount?: number,
  ) {

    const { transactionsWithAvailability, transactionsAvailable } = await this.getTransactionsAvailableForInvoice(invoice);

    if (transactionsAvailable) {
      this.dialogService.open(ApplyTransactionComponent, {
        header: 'Apply Existing Transaction to Invoice?',
        width: this.responsiveHelper.dialogWidth,
        data: { transactionsWithAvailability, invoice },
        contentStyle: this.freyaHelper.getDialogContentStyle('1.5rem'),
      });
    } else {
      this.openCreateTransactionsDialogForInvoice(invoice, defaultAmount);
    }

  }

  /** Collect money against one invoice. See `pay-invoice.effects`. */
  openCreateTransactionsDialogForInvoice(
    invoice: BaseInvoiceFragment,
    defaultAmount?: number,
  ) {
    this.store.dispatch(payInvoiceRequested({ invoice, defaultAmount }));
  }

  async getTransactionsAvailableForInvoice(invoice: BaseInvoiceFragment) {

    const jobTransactions$ = this.transactionsGQL.fetch({
      filter: {
        jobId: invoice.job.id,
        showDeleted: false,
      },
      limit: -1,
    }, { fetchPolicy: 'network-only' }).pipe(
      map(res => res.data.transactions.transactions),
    );

    const transactions = await jobTransactions$.toPromise();

    return this.freyaHelper.getTransactionsAvailableForInvoice(invoice, transactions);
  }
}
